/**
 * Footer of the Chrome extension side panel / Outlook task pane. Left: Back, on a screen past the
 * first one (the job wizard; see extensionBack.ts). Right: the signed-in user's avatar, which
 * opens a menu with who is signed in, to which firm, and Sign out. The panel has no header or
 * user menu of its own.
 */
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth';
import { useTheme } from '@/theme/useTheme';
import { useTranslation } from '@/i18n/useTranslation';
import { useExtensionBack } from '@/utils/extensionBack';
import { useNetDocumentsAuth } from '@/features/step-detail/api/useNetDocuments';
import ndRedIcon from '@/assets/images/icon_ndred.png';
import ndBlueIcon from '@/assets/images/icon_ndblue.png';

const initials = (name: string) =>
  name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || '?';

export const ExtensionAccountBar = () => {
  const { user, logout, currentTenantName, hasPermission } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const back = useExtensionBack();
  // NetDocuments connection, as the sidebar's icon shows it (blue connected, red not): the panel
  // has no sidebar. Clicking it opens the NetDocuments sign-in, as the sidebar icon does.
  const allowNd = hasPermission('motr.Application.AllowNDConnection');
  const { isAuthenticated: ndConnected, isAuthenticating: ndConnecting, authenticate: ndConnect } = useNetDocumentsAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLButtonElement>(null);

  // The menu closes on a click outside it or Escape (focus returns to the avatar).
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !avatarRef.current?.contains(target)) setMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setMenuOpen(false); avatarRef.current?.focus(); }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  if (!user) return null;

  const profile = user.profile;
  const fullName = [profile.given_name, profile.family_name].filter(Boolean).join(' ');
  const name = fullName || profile.name || profile.preferred_username || profile.email || '';
  const email = profile.email && profile.email !== name ? profile.email : '';
  const firm = currentTenantName || t('Common:HostAccount', { defaultValue: 'Host' });

  const textButton = {
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 8px',
    border: 'none', borderRadius: theme.borderRadius.md, background: 'none', cursor: 'pointer',
    color: theme.colors.textSecondary, fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
  } as const;

  return (
    <footer
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        minHeight: '44px',
        padding: '6px 12px',
        borderTop: `1px solid ${theme.colors.border}`,
        backgroundColor: theme.colors.backgroundCard,
        fontSize: '12px',
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
        {back && (
          <button
            type="button"
            onClick={back}
            style={{ ...textButton, marginLeft: '-8px', color: theme.colors.textPrimary }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            {t('Common:Back', { defaultValue: 'Back' })}
          </button>
        )}
      </div>

      {allowNd && (
        <button
          type="button"
          onClick={ndConnect}
          aria-label={ndConnected
            ? t('Common:NdReconnect', { defaultValue: 'Connected to NetDocuments. Sign in again' })
            : t('Common:NdConnect', { defaultValue: 'Connect to NetDocuments' })}
          title={ndConnected
            ? t('Common:NdConnected', { defaultValue: 'Connected to NetDocuments' })
            : ndConnecting
              ? t('Common:NdConnecting', { defaultValue: 'Connecting to NetDocuments…' })
              : t('Common:NdConnect', { defaultValue: 'Connect to NetDocuments' })}
          style={{
            flexShrink: 0, display: 'grid', placeItems: 'center', width: 30, height: 30, padding: 0,
            border: 'none', borderRadius: theme.borderRadius.md, background: 'none',
            cursor: 'pointer',
            opacity: ndConnecting ? 0.5 : 1,
          }}
        >
          <img src={ndConnected ? ndBlueIcon : ndRedIcon} alt="" style={{ maxWidth: 24, maxHeight: 24 }} />
        </button>
      )}

      <button
        ref={avatarRef}
        type="button"
        onClick={() => setMenuOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={t('Common:AccountMenu', { defaultValue: 'Account: {{name}}', name })}
        title={name}
        style={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
          padding: 0, border: 'none', cursor: 'pointer',
          backgroundColor: theme.colors.brandPrimary, color: '#fff', fontWeight: 700, fontSize: '11px',
          boxShadow: menuOpen ? `0 0 0 2px ${theme.colors.backgroundCard}, 0 0 0 4px ${theme.colors.brandPrimary}` : 'none',
        }}
      >
        {initials(name)}
      </button>

      {menuOpen && (
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'absolute', right: '12px', bottom: 'calc(100% + 6px)', zIndex: 20,
            width: 'min(280px, calc(100vw - 24px))',
            backgroundColor: theme.colors.backgroundCard,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.lg,
            boxShadow: '0 10px 30px rgba(15, 23, 42, .18)',
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '14px' }}>
            <div
              aria-hidden="true"
              style={{
                width: 38, height: 38, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
                backgroundColor: theme.colors.brandPrimary, color: '#fff', fontWeight: 700, fontSize: '13px',
              }}
            >
              {initials(name)}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '13px', color: theme.colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name}
              </div>
              {email && (
                <div style={{ color: theme.colors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</div>
              )}
              <span
                title={t('Common:Firm', { defaultValue: 'Firm' })}
                style={{
                  display: 'inline-block', marginTop: '4px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  padding: '1px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
                  backgroundColor: theme.colors.infoLight, color: theme.colors.brandAccent,
                }}
              >
                {firm}
              </span>
            </div>
          </div>
          <div style={{ borderTop: `1px solid ${theme.colors.border}`, padding: '4px' }}>
            <button
              type="button"
              role="menuitem"
              onClick={() => { setMenuOpen(false); void logout(); }}
              style={{ ...textButton, width: '100%', padding: '8px 10px', color: theme.colors.textPrimary, fontSize: '13px' }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 17l5-5-5-5" /><path d="M20 12H9" /><path d="M12 20H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h7" />
              </svg>
              {t('Common:SignOut', { defaultValue: 'Sign out' })}
            </button>
          </div>
        </div>
      )}
    </footer>
  );
};
